import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lightbulb, Shield, GitBranch, RefreshCw } from 'lucide-react';
import { PortfolioHolding } from '@/hooks/usePortfolioHoldings';
import { useCorrelations } from '@/hooks/useCorrelations';

interface PortfolioInsightsProps {
  holdings: PortfolioHolding[];
  totalValue: number;
}

export const PortfolioInsights = ({ holdings, totalValue }: PortfolioInsightsProps) => {
  const { correlations } = useCorrelations();

  // Diversification Score 0-100
  const diversificationScore = useMemo(() => {
    if (holdings.length === 0) return 0;
    const uniqueSectors = new Set(holdings.map(h => h.sector || 'Other')).size;
    const uniqueAssetTypes = new Set(holdings.map(h => h.asset_type)).size;
    const maxHoldingPct = totalValue > 0
      ? Math.max(...holdings.map(h => (h.market_value || 0) / totalValue))
      : 1;
    return Math.min(100, Math.round(
      ((uniqueSectors / 11) * 0.4 + (uniqueAssetTypes / 6) * 0.3 + (1 - maxHoldingPct) * 0.3) * 100
    ));
  }, [holdings, totalValue]);

  // Risk Profile
  const riskProfile = useMemo(() => {
    if (holdings.length === 0) return { label: 'Unknown', color: 'text-muted-foreground' };
    const cryptoPct = totalValue > 0 ? holdings.filter(h => h.asset_type === 'crypto').reduce((s, h) => s + (h.market_value || 0), 0) / totalValue : 0;
    const stockPct = totalValue > 0 ? holdings.filter(h => h.asset_type === 'stock').reduce((s, h) => s + (h.market_value || 0), 0) / totalValue : 0;
    const bondForexPct = totalValue > 0 ? holdings.filter(h => h.asset_type === 'bond' || h.asset_type === 'forex').reduce((s, h) => s + (h.market_value || 0), 0) / totalValue : 0;

    if (cryptoPct > 0.3 || stockPct > 0.8) return { label: 'Aggressive', color: 'text-[hsl(var(--accent-red))]' };
    if (bondForexPct > 0.5 && cryptoPct < 0.2) return { label: 'Conservative', color: 'text-[hsl(var(--accent-green))]' };
    return { label: 'Moderate', color: 'text-[hsl(var(--accent-amber))]' };
  }, [holdings, totalValue]);

  // Correlation insights
  const correlationInsights = useMemo(() => {
    if (!correlations.length || holdings.length < 2) return [];
    const symbols = new Set(holdings.map(h => h.symbol));
    const insights: string[] = [];
    for (const c of correlations) {
      if (symbols.has(c.symbol_a) && symbols.has(c.symbol_b) && c.correlation > 0.7) {
        insights.push(`${c.symbol_a} and ${c.symbol_b} are ${(c.correlation * 100).toFixed(0)}% correlated. A downturn in one affects both.`);
      }
      if (insights.length >= 3) break;
    }
    return insights;
  }, [correlations, holdings]);

  // Rebalance suggestions
  const rebalanceSuggestions = useMemo(() => {
    const suggestions: string[] = [];
    if (holdings.length === 0) return suggestions;
    const sectorMap: Record<string, number> = {};
    holdings.filter(h => h.asset_type === 'stock').forEach(h => {
      sectorMap[h.sector || 'Other'] = (sectorMap[h.sector || 'Other'] || 0) + (h.market_value || 0);
    });
    for (const [sector, val] of Object.entries(sectorMap)) {
      if (totalValue > 0 && val / totalValue > 0.3) {
        suggestions.push(`Consider reducing ${sector} exposure from ${((val / totalValue) * 100).toFixed(0)}% to ~25%`);
      }
    }
    const hasBonds = holdings.some(h => h.asset_type === 'bond');
    if (!hasBonds && holdings.length >= 3) {
      suggestions.push('Adding bonds or stablecoins could reduce portfolio volatility');
    }
    return suggestions.slice(0, 3);
  }, [holdings, totalValue]);

  // Gauge SVG (mini version)
  const GaugeMini = ({ score }: { score: number }) => {
    const color = score >= 70 ? 'hsl(var(--accent-green))' : score >= 40 ? 'hsl(var(--accent-amber))' : 'hsl(var(--accent-red))';
    const size = 64;
    const cx = size / 2;
    const cy = size / 2;
    const r = 24;
    const sw = 5;
    const startAngle = 135;
    const totalSweep = 270;
    const endAngle = startAngle + (score / 100) * totalSweep;
    const toRad = (d: number) => (d * Math.PI) / 180;
    const arc = (s: number, e: number) => {
      const x1 = cx + r * Math.cos(toRad(s));
      const y1 = cy + r * Math.sin(toRad(s));
      const x2 = cx + r * Math.cos(toRad(e));
      const y2 = cy + r * Math.sin(toRad(e));
      return `M ${x1} ${y1} A ${r} ${r} 0 ${e - s > 180 ? 1 : 0} 1 ${x2} ${y2}`;
    };

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <path d={arc(startAngle, startAngle + totalSweep)} fill="none" stroke="hsl(var(--muted))" strokeWidth={sw} strokeLinecap="round" />
        {score > 0 && <path d={arc(startAngle, endAngle)} fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" />}
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={16} fontWeight={700} fill={color}>{score}</text>
      </svg>
    );
  };

  return (
    <Card className="border-border/30">
      <CardHeader className="pb-2 px-4 pt-4">
        <CardTitle className="text-sm font-heading flex items-center gap-1.5">
          <Lightbulb className="w-4 h-4 text-[hsl(var(--accent-amber))]" />
          Portfolio Insights
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-4">
        {/* Diversification + Risk */}
        <div className="flex items-center gap-4">
          <GaugeMini score={diversificationScore} />
          <div className="flex-1 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Diversification</span>
              <span className="text-xs font-mono font-semibold">{diversificationScore}/100</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Risk Profile</span>
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 border-0 ${riskProfile.color}`}>
                <Shield className="w-3 h-3 mr-1" />
                {riskProfile.label}
              </Badge>
            </div>
          </div>
        </div>

        {/* Correlation Insights */}
        {correlationInsights.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <GitBranch className="w-3 h-3" /> Correlations
            </p>
            {correlationInsights.map((insight, i) => (
              <p key={i} className="text-[11px] text-muted-foreground leading-tight pl-4">• {insight}</p>
            ))}
          </div>
        )}

        {/* Rebalance Suggestions */}
        {rebalanceSuggestions.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Rebalance
            </p>
            {rebalanceSuggestions.map((s, i) => (
              <p key={i} className="text-[11px] text-muted-foreground leading-tight pl-4">• {s}</p>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
